/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './Share.scss';

/**
 * This is the Share component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Share({ url, button, color, copy, copied, email }) {
  const input = useRef(null);
  const response = useRef(null);
  const animation = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onFinish() {
      animation.current.pause();
      animation.current.currentTime = 1;
      setOpen(false);
    }

    if (response.current) {
      animation.current = response.current.animate(
        [{ opacity: 0 }, { opacity: 1 }, { opacity: 0 }],
        {
          duration: 1000,
          iterations: 1,
          easing: 'ease-in-out',
        }
      );

      animation.current.addEventListener('finish', onFinish);

      animation.current.pause();
      animation.current.currentTime = 1;

      return () => {
        animation.current.removeEventListener('finish', onFinish);
        animation.current.cancel();
      };
    }
  }, [open]);

  function onCopy() {
    const prev = document.activeElement;
    input.current.select();
    if (document.execCommand('copy')) {
      if (animation.current) {
        animation.current.play();
      }
    }
    prev.focus();
  }

  function renderOptions() {
    if (!open) return null;

    return (
      <div className="options">
        <button onClick={onCopy}>
          {copy}{' '}
          <span ref={response} className="copied">
            {copied}
          </span>
        </button>
        <a href={`mailto:?subject=foo&body=${url}`}>{email}</a>
      </div>
    );
  }

  return (
    <div className="share">
      {renderOptions()}
      <button
        className={`button ${color}`}
        type="button"
        onClick={() => {
          setOpen(open => !open);
        }}
      >
        {button}
      </button>
      <div className="url">
        <input ref={input} type="text" value={url} onChange={() => {}} />
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Share.propTypes = {
  url: PropTypes.string.isRequired,
  button: PropTypes.string.isRequired,
  color: PropTypes.string,
  copy: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Share.defaultProps = {
  url: location.href,
  button: 'Share',
  color: 'red',
  copy: 'Copy link',
  email: 'Email',
};

export default Share;
